import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Redirect } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";

const ACCENT = COLORS.primary_color_shade6 || "#039BE5";
const MODAL_RADIUS = 16;

function parseDateOnly(value) {
  if (!value) return null;
  const parts = String(value).slice(0, 10).split("-");
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toInputDate(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function getReferenceSession(sessions) {
  if (!Array.isArray(sessions) || sessions.length === 0) return null;

  const current = sessions.find(
    (s) => String(s.status || "").toLowerCase() === "current"
  );
  if (current) return current;

  const previous = sessions.find(
    (s) => String(s.status || "").toLowerCase() === "previous"
  );
  if (previous) return previous;

  const nonUpcoming = sessions.filter(
    (s) => String(s.status || "").toLowerCase() !== "upcoming"
  );
  const pool = nonUpcoming.length ? nonUpcoming : sessions;

  return [...pool].sort((a, b) => {
    const aEnd = parseDateOnly(a.end_date)?.getTime() || 0;
    const bEnd = parseDateOnly(b.end_date)?.getTime() || 0;
    return bEnd - aEnd;
  })[0];
}

function deriveNextSessionName(previousName) {
  if (!previousName) return "";

  const trimmed = String(previousName).trim();
  const fullRange = trimmed.match(/^(\d{4})\s*[-–/]\s*(\d{4})$/);
  if (fullRange) {
    return `${Number(fullRange[1]) + 1}-${Number(fullRange[2]) + 1}`;
  }

  const shortRange = trimmed.match(/^(\d{4})\s*[-–/]\s*(\d{2})$/);
  if (shortRange) {
    const nextStart = Number(shortRange[1]) + 1;
    const nextEnd = (Number(shortRange[2]) + 1) % 100;
    return `${nextStart}-${String(nextEnd).padStart(2, "0")}`;
  }

  const singleYear = trimmed.match(/^(\d{4})$/);
  if (singleYear) {
    return String(Number(singleYear[1]) + 1);
  }

  return trimmed.replace(/\b(19|20)\d{2}\b/g, (year) =>
    String(Number(year) + 1)
  );
}

function deriveNextSessionDates(previousStart, previousEnd) {
  const prevEnd = parseDateOnly(previousEnd);
  const prevStart = parseDateOnly(previousStart);
  if (!prevEnd) {
    return { startDate: "", endDate: "" };
  }

  const nextStart = addDays(prevEnd, 1);
  let nextEnd;

  if (prevStart) {
    const durationMs = prevEnd.getTime() - prevStart.getTime();
    nextEnd = new Date(nextStart.getTime() + durationMs);
  } else {
    nextEnd = new Date(
      prevEnd.getFullYear() + 1,
      prevEnd.getMonth(),
      prevEnd.getDate()
    );
  }

  return {
    startDate: toInputDate(nextStart),
    endDate: toInputDate(nextEnd),
  };
}

function buildNextSessionDraft(sessions) {
  const reference = getReferenceSession(sessions);
  if (!reference) {
    return {
      sessionName: "",
      startDate: "",
      endDate: "",
      referenceName: "",
    };
  }

  const { startDate, endDate } = deriveNextSessionDates(
    reference.start_date,
    reference.end_date
  );

  return {
    sessionName: deriveNextSessionName(reference.session_name),
    startDate,
    endDate,
    referenceName: reference.session_name || "",
  };
}

function parseApiPayload(data) {
  if (!data) return null;
  if (typeof data === "object") return data;
  if (typeof data !== "string") return null;

  try {
    return JSON.parse(data);
  } catch {
    const match = data.match(/\{[\s\S]*\}/);
    if (!match) return null;
    try {
      return JSON.parse(match[0]);
    } catch {
      return null;
    }
  }
}

function isApiOk(payload) {
  return String(payload?.api_status || "").trim().toUpperCase() === "OK";
}

async function verifySessionCreated(schoolCode, sessionName, startDate, endDate) {
  const url =
    process.env.REACT_APP_SERVER_NAME +
    process.env.REACT_APP_TARGET_DIR +
    "getSessionSettings.php";

  const response = await axios.get(url, {
    params: { school_code: schoolCode },
  });

  const payload = parseApiPayload(response.data);
  if (!isApiOk(payload)) return false;

  const list = Array.isArray(payload.data) ? payload.data : [];
  const normalizedName = sessionName.trim().toLowerCase();

  return list.some((session) => {
    const sameName =
      String(session.session_name || "").trim().toLowerCase() === normalizedName;
    const sameStart = String(session.start_date || "").slice(0, 10) === startDate;
    const sameEnd = String(session.end_date || "").slice(0, 10) === endDate;
    return sameName && sameStart && sameEnd;
  });
}

function finishAddSession(payload, action2, handleClose, notifyWarning = false) {
  const copyOk = payload?.data?.copy_ok !== false;
  if (notifyWarning || !copyOk) {
    myToast.notify_warning(
      payload?.api_remark ||
        "Session added, but some setup data may need manual review."
    );
  } else {
    myToast.notify_success(
      payload?.api_remark || "Session added successfully!"
    );
  }
  if (typeof action2 === "function") {
    action2();
  }
  handleClose();
}

const MODAL_STYLES = `
  .mdl-add-session.modal-dialog {
    margin: 0.75rem auto 1.25rem;
    max-height: calc(100vh - 1.5rem);
    display: flex;
    align-items: center;
  }
  .mdl-add-session .modal-content {
    border: none;
    border-radius: ${MODAL_RADIUS}px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  }
  .mdl-add-session .modal-header {
    border-top-left-radius: ${MODAL_RADIUS}px;
    border-top-right-radius: ${MODAL_RADIUS}px;
  }
  .mdl-add-session .modal-footer {
    border-bottom-left-radius: ${MODAL_RADIUS}px;
    border-bottom-right-radius: ${MODAL_RADIUS}px;
  }
  .mdl-add-session .form-control {
    border-radius: 10px;
    border: 1px solid #e5e7eb;
  }
  .mdl-add-session .form-control:focus {
    border-color: ${ACCENT};
    box-shadow: 0 0 0 3px color-mix(in srgb, ${ACCENT} 18%, transparent);
  }
  .mdl-add-session .modal-header .btn-close {
    filter: brightness(0) invert(1);
    opacity: 0.9;
  }
`;

const MdlAddSession = ({ actionClose, action2, sessions = [] }) => {
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(true);
  const draft = useMemo(() => buildNextSessionDraft(sessions), [sessions]);
  const [sessionName, setSessionName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setSessionName(draft.sessionName);
    setStartDate(draft.startDate);
    setEndDate(draft.endDate);
    setError("");
  }, [draft]);

  const handleClose = () => {
    setShow(false);
    actionClose();
  };

  const handleSubmit = async () => {
    if (!sessionName.trim()) {
      setError("Please enter a session name.");
      return;
    }
    if (!startDate) {
      setError("Please select a start date.");
      return;
    }
    if (!endDate) {
      setError("Please select an end date.");
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError("End date cannot be before start date.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const schoolCode = SessionManager.getSchoolCode();
      const url =
        process.env.REACT_APP_SERVER_NAME +
        process.env.REACT_APP_TARGET_DIR +
        "AddNewUpcomingSession.php";

      const response = await axios.get(url, {
        params: {
          school_code: schoolCode,
          session_name: sessionName.trim(),
          start_date: startDate,
          end_date: endDate,
        },
      });

      const payload = parseApiPayload(response.data);

      if (isApiOk(payload)) {
        finishAddSession(payload, action2, handleClose);
        return;
      }

      const created = await verifySessionCreated(
        schoolCode,
        sessionName.trim(),
        startDate,
        endDate
      );

      if (created) {
        finishAddSession(
          {
            api_remark: "Session added successfully!",
            data: { copy_ok: payload?.data?.copy_ok },
          },
          action2,
          handleClose,
          payload?.data?.copy_ok === false
        );
        return;
      }

      myToast.notify_warning(
        payload?.api_remark || "Could not add session. Please try again."
      );
    } catch (err) {
      console.error("Add session error:", err);
      myToast.notify_error("An error occurred while adding the session.");
    } finally {
      setLoading(false);
    }
  };

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <>
      <style>{MODAL_STYLES}</style>
      <Modal
        show={show}
        onHide={handleClose}
        animation={false}
        size="lg"
        dialogClassName="mdl-add-session"
        aria-labelledby="mdl-add-session-title"
        centered
      >
      <Modal.Header
        closeButton
        style={{
          background: `linear-gradient(135deg, ${ACCENT} 0%, ${COLORS.primary_color_shade7 || "#0288D1"} 100%)`,
          borderBottom: "none",
          color: "#fff",
        }}
      >
        <Modal.Title
          id="mdl-add-session-title"
          style={{ fontWeight: 800, color: "#fff" }}
        >
          <i className="fa fa-calendar-plus-o" style={{ marginRight: 8 }} />
          Add New Session
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ paddingTop: 18, paddingBottom: 8 }}>
        {loading ? (
          <MyLoader />
        ) : (
          <div className="row g-3">
            {draft.referenceName && (
              <div className="col-md-12">
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#0369a1",
                    padding: "8px 12px",
                    borderRadius: 10,
                    background: "color-mix(in srgb, #0ea5e9 10%, #ffffff)",
                    border: "1px solid color-mix(in srgb, #0ea5e9 22%, #e5e7eb)",
                  }}
                >
                  <i className="fa fa-info-circle" style={{ marginRight: 6 }} />
                  Prefilled from previous session:{" "}
                  <strong>{draft.referenceName}</strong>
                </div>
              </div>
            )}

            <div className="col-md-12">
              <Form.Label
                style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}
              >
                Session Name
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. 2026-2027"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                style={{ fontSize: 15, padding: "10px 12px", borderRadius: 10 }}
                autoFocus
              />
            </div>

            <div className="col-md-6">
              <Form.Label
                style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}
              >
                Start Date
              </Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ fontSize: 15, padding: "10px 12px", borderRadius: 10 }}
              />
            </div>

            <div className="col-md-6">
              <Form.Label
                style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}
              >
                End Date
              </Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ fontSize: 15, padding: "10px 12px", borderRadius: 10 }}
              />
            </div>

            {error && (
              <div className="col-md-12">
                <div
                  style={{
                    color: "#dc2626",
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "8px 12px",
                    borderRadius: 8,
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                  }}
                >
                  <i
                    className="fa fa-exclamation-circle"
                    style={{ marginRight: 6 }}
                  />
                  {error}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: "1px solid #e5e7eb" }}>
        <div className="d-flex justify-content-end w-100 gap-2">
          <Button
            variant="light"
            onClick={handleClose}
            disabled={loading}
            style={{
              fontWeight: 700,
              padding: "8px 14px",
              borderRadius: 999,
              border: "1px solid #e5e7eb",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            style={{
              fontWeight: 700,
              padding: "8px 16px",
              borderRadius: 999,
              background: ACCENT,
              borderColor: ACCENT,
            }}
          >
            <i className="fa fa-plus" style={{ marginRight: 6 }} />
            Add Session
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default MdlAddSession;
